import React, { useState } from 'react';
import {
  Share,
  Heart,
  MessageCircle,
  Users,
  Camera,
  Video,
  Plus,
  Search,
  Filter,
  TrendingUp,
  ThumbsUp,
  Eye,
  Calendar,
  MapPin
} from 'lucide-react';

interface SocialHubProps {
  memberId: string;
}

// Composant pour une publication
const PostCard: React.FC<{post: any, onLike: (postId: number) => void, onComment: (postId: number) => void}> = ({ post, onLike, onComment }) => {
  const [showComments, setShowComments] = useState(false);

  const formatTimeAgo = (date: string) => {
    const now = new Date();
    const postDate = new Date(date);
    const diffInMinutes = Math.floor((now.getTime() - postDate.getTime()) / (1000 * 60));

    if (diffInMinutes < 60) return `Il y a ${diffInMinutes} min`;
    if (diffInMinutes < 1440) return `Il y a ${Math.floor(diffInMinutes / 60)}h`;
    return `Il y a ${Math.floor(diffInMinutes / 1440)} jour(s)`;
  };

  return (
    <div className="card-udb p-6 mb-6">
      {/* Header du post */}
      <div className="flex items-start space-x-3 mb-4">
        <div className="w-12 h-12 bg-primary-blue/10 rounded-full flex items-center justify-center">
          <Users className="text-primary-blue" size={20} />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">{post.author.name}</h3>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>{post.author.role}</span>
                <span>•</span>
                <span>{formatTimeAgo(post.created_at)}</span>
                {post.location && (
                  <>
                    <span>•</span>
                    <div className="flex items-center gap-1">
                      <MapPin size={12} />
                      <span>{post.location}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            {post.is_pinned && (
              <span className="bg-primary-yellow/20 text-primary-blue text-xs px-2 py-1 font-medium">
                Épinglé
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Contenu du post */}
      <div className="mb-4">
        <p className="text-gray-800 leading-relaxed mb-3">{post.content}</p>

        {/* Image, vidéo ou YouTube */}
        {post.media && (
          <div className="rounded-lg overflow-hidden bg-gray-100">
            {post.media.type === 'image' ? (
              <img
                src={post.media.url}
                alt={post.media.alt || 'Image de publication'}
                className="w-full h-auto max-h-96 object-cover"
              />
            ) : post.media.type === 'video' ? (
              <video
                src={post.media.url}
                controls
                className="w-full h-auto max-h-96"
                poster={post.media.thumbnail}
              >
                Votre navigateur ne supporte pas la vidéo.
              </video>
            ) : post.media.type === 'youtube' ? (
              <div className="relative aspect-video">
                <iframe
                  src={`https://www.youtube.com/embed/${post.media.video_id}`}
                  title={post.media.title || 'Vidéo YouTube'}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : null}
          </div>
        )}

        {/* Tags d'événement */}
        {post.event && (
          <div className="mt-3 p-3 bg-primary-blue/5 border-l-4 border-primary-blue">
            <div className="flex items-center gap-2 text-sm text-primary-blue font-medium">
              <Calendar size={16} />
              <span>{post.event.title}</span>
            </div>
            <div className="text-sm text-gray-600 mt-1">
              {new Date(post.event.date).toLocaleDateString('fr-FR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>
        )}
      </div>

      {/* Stats du post */}
      <div className="flex items-center justify-between py-2 border-t border-b border-gray-100 mb-3">
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <span>{post.likes_count} mentions j'aime</span>
          <span>{post.comments_count} commentaires</span>
          <span>{post.shares_count} partages</span>
        </div>
        <div className="flex items-center gap-1 text-sm text-gray-500">
          <Eye size={14} />
          <span>{post.views_count}</span>
        </div>
      </div>

      {/* Boutons d'action */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => onLike(post.id)}
            className={`flex items-center gap-2 px-4 py-2 transition-colors ${
              post.user_liked
                ? 'text-primary-blue bg-primary-blue/10'
                : 'text-gray-600 hover:text-primary-blue hover:bg-primary-blue/5'
            }`}
          >
            <ThumbsUp size={18} />
            <span className="text-sm font-medium">J'aime</span>
          </button>
          <button
            onClick={() => {
              setShowComments(!showComments);
              onComment(post.id);
            }}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-primary-blue hover:bg-primary-blue/5 transition-colors"
          >
            <MessageCircle size={18} />
            <span className="text-sm font-medium">Commenter</span>
          </button>
          <button className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-primary-green hover:bg-primary-green/5 transition-colors">
            <Share size={18} />
            <span className="text-sm font-medium">Partager</span>
          </button>
        </div>
      </div>

      {/* Section commentaires */}
      {showComments && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-start space-x-3 mb-3">
            <div className="w-8 h-8 bg-primary-green/10 rounded-full flex items-center justify-center">
              <Users className="text-primary-green" size={16} />
            </div>
            <div className="flex-1">
              <input
                type="text"
                placeholder="Écrivez un commentaire..."
                className="w-full px-3 py-2 border border-gray-200 focus:border-primary-blue outline-none transition-colors text-sm"
              />
            </div>
          </div>

          {/* Commentaires existants */}
          {post.recent_comments && post.recent_comments.map((comment: any) => (
            <div key={comment.id} className="flex items-start space-x-3 mb-3">
              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                <Users className="text-gray-500" size={14} />
              </div>
              <div className="flex-1">
                <div className="bg-gray-50 px-3 py-2 inline-block">
                  <div className="font-medium text-sm text-gray-900">{comment.author.name}</div>
                  <div className="text-sm text-gray-700">{comment.content}</div>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {formatTimeAgo(comment.created_at)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Composant pour créer un nouveau post
const CreatePostCard: React.FC<{onCreatePost: (content: string, media?: any) => void}> = ({ onCreatePost }) => {
  const [content, setContent] = useState('');
  const [showMediaOptions, setShowMediaOptions] = useState(false);

  const handleSubmit = () => {
    if (content.trim()) {
      onCreatePost(content);
      setContent('');
      setShowMediaOptions(false);
    }
  };

  return (
    <div className="card-udb p-6 mb-6">
      <div className="flex items-start space-x-3">
        <div className="w-12 h-12 bg-primary-blue/10 rounded-full flex items-center justify-center">
          <Users className="text-primary-blue" size={20} />
        </div>
        <div className="flex-1">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Quoi de neuf dans votre engagement politique ?"
            className="w-full p-3 border border-gray-200 focus:border-primary-blue outline-none transition-colors resize-none"
            rows={3}
          />

          {showMediaOptions && (
            <div className="mt-3 p-3 border border-gray-200 bg-gray-50">
              <div className="flex items-center gap-4">
                <button className="flex items-center gap-2 px-3 py-2 text-primary-blue hover:bg-primary-blue/10 transition-colors">
                  <Camera size={18} />
                  <span className="text-sm">Photo</span>
                </button>
                <button className="flex items-center gap-2 px-3 py-2 text-primary-blue hover:bg-primary-blue/10 transition-colors">
                  <Video size={18} />
                  <span className="text-sm">Vidéo</span>
                </button>
                <button className="flex items-center gap-2 px-3 py-2 text-primary-blue hover:bg-primary-blue/10 transition-colors">
                  <Calendar size={18} />
                  <span className="text-sm">Événement</span>
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowMediaOptions(!showMediaOptions)}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-primary-blue hover:bg-primary-blue/5 transition-colors"
              >
                <Plus size={18} />
                <span className="text-sm">Ajouter média</span>
              </button>
            </div>
            <button
              onClick={handleSubmit}
              disabled={!content.trim()}
              className="btn-udb-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Publier
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SocialHub: React.FC<SocialHubProps> = ({ memberId }) => {
  const [activeTab, setActiveTab] = useState('feed');
  const [searchQuery, setSearchQuery] = useState('');

  // Données mockées pour les publications
  const mockPosts = [
    {
      id: 0,
      content: "🎬 TOURNÉE DU SECRÉTARIAT GÉNÉRAL DE L'UDB EN OGOOUÉ - LOLO 🎬\n\nDécouvrez les moments forts de notre tournée dans la province de l'Ogooué-Lolo ! Rencontres avec les populations locales, présentation de nos projets de développement et échanges constructifs avec nos militants sur le terrain.",
      author: {
        name: "Secrétariat Général UDB",
        role: "Communication Officielle"
      },
      created_at: "2025-09-15T14:00:00Z",
      location: "Ogooué-Lolo",
      likes_count: 128,
      comments_count: 34,
      shares_count: 67,
      views_count: 892,
      user_liked: false,
      is_pinned: true,
      media: {
        type: 'youtube',
        url: 'https://www.youtube.com/watch?v=ohIYvtmU4iY',
        thumbnail: 'https://img.youtube.com/vi/ohIYvtmU4iY/maxresdefault.jpg',
        video_id: 'ohIYvtmU4iY',
        title: 'TOURNÉE DU SECRÉTARIAT GÉNÉRAL DE L\'UDB EN OGOOUÉ - LOLO'
      },
      recent_comments: [
        {
          id: 1,
          content: "Merci pour cette belle présentation de notre province ! L'UDB nous redonne espoir.",
          author: { name: "Joseph Makaya" },
          created_at: "2025-09-15T14:30:00Z"
        },
        {
          id: 2,
          content: "Excellente initiative ! Continuez ce travail de proximité avec les populations.",
          author: { name: "Martine Bivigou" },
          created_at: "2025-09-15T15:00:00Z"
        }
      ]
    },
    {
      id: 1,
      content: "Excellente mobilisation aujourd'hui lors de notre campagne de sensibilisation à Libreville ! Plus de 200 citoyens ont participé à nos discussions sur l'avenir de notre pays. Ensemble, nous bâtissons le Gabon de demain ! 🇬🇦",
      author: {
        name: "Marie Ndong",
        role: "Responsable Communication Estuaire"
      },
      created_at: "2025-09-15T10:30:00Z",
      location: "Libreville, Estuaire",
      likes_count: 45,
      comments_count: 12,
      shares_count: 8,
      views_count: 156,
      user_liked: false,
      is_pinned: true,
      media: {
        type: 'image',
        url: '/api/placeholder/600/400',
        alt: 'Mobilisation à Libreville'
      },
      recent_comments: [
        {
          id: 1,
          content: "Bravo pour cette initiative ! Le changement commence par nous.",
          author: { name: "Jean Mboumba" },
          created_at: "2025-09-15T11:00:00Z"
        }
      ]
    },
    {
      id: 2,
      content: "Formation en leadership politique ce weekend à Port-Gentil. Nous avons besoin de leaders engagés dans chaque communauté. Inscrivez-vous dès maintenant !",
      author: {
        name: "Paul Obame",
        role: "Coordinateur Formation"
      },
      created_at: "2025-09-15T08:15:00Z",
      location: "Port-Gentil, Ogooué-Maritime",
      likes_count: 23,
      comments_count: 5,
      shares_count: 15,
      views_count: 89,
      user_liked: true,
      is_pinned: false,
      event: {
        title: "Formation Leadership Politique",
        date: "2025-09-20T09:00:00Z"
      },
      recent_comments: []
    },
    {
      id: 3,
      content: "Fier de voir notre projet de cantine scolaire à Franceville prendre forme ! Grâce à vos contributions, 300 enfants auront bientôt accès à des repas nutritifs quotidiens. Voici les dernières images du chantier.",
      author: {
        name: "Sylvie Boussamba",
        role: "Chef de projet Haut-Ogooué"
      },
      created_at: "2025-09-14T16:45:00Z",
      location: "Franceville, Haut-Ogooué",
      likes_count: 67,
      comments_count: 18,
      shares_count: 25,
      views_count: 234,
      user_liked: false,
      is_pinned: false,
      media: {
        type: 'video',
        url: '/api/placeholder-video/600/400',
        thumbnail: '/api/placeholder/600/400'
      },
      recent_comments: [
        {
          id: 2,
          content: "Magnifique projet ! Nos enfants méritent le meilleur.",
          author: { name: "Claire Moussavou" },
          created_at: "2025-09-14T17:00:00Z"
        },
        {
          id: 3,
          content: "Comment puis-je contribuer à ce projet ?",
          author: { name: "Michel Ondo" },
          created_at: "2025-09-14T18:30:00Z"
        }
      ]
    }
  ];

  const handleLike = (postId: number) => {
    console.log('Like post:', postId);
  };

  const handleComment = (postId: number) => {
    console.log('Comment on post:', postId);
  };

  const handleCreatePost = (content: string, media?: any) => {
    console.log('Create new post:', content, media);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const stats = [
    { label: 'Publications cette semaine', value: '24', icon: Share },
    { label: 'Membres actifs', value: '1,247', icon: Users },
    { label: 'Interactions totales', value: '3,567', icon: Heart },
    { label: 'Portée moyenne', value: '12K', icon: Eye }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Header Section - Style UDB */}
      <section className="relative bg-primary-blue text-white overflow-hidden">
        <div className="absolute inset-0">
          {/* Particules flottantes */}
          <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full animate-float-up"
                style={{
                  width: Math.random() * 3 + 1 + 'px',
                  height: Math.random() * 3 + 1 + 'px',
                  left: Math.random() * 100 + '%',
                  bottom: '-20px',
                  backgroundColor: i % 3 === 0 ? '#ffd400' : i % 3 === 1 ? '#ffffff' : '#00469b',
                  opacity: Math.random() * 0.6 + 0.2,
                  animationDuration: `${Math.random() * 15 + 10}s`,
                  animationDelay: `${Math.random() * 10}s`,
                }}
              />
            ))}
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            {/* Titre principal */}
            <div>
              <h1 className="text-2xl md:text-3xl font-light text-white">
                Connectés pour
                <span className="text-primary-yellow font-normal ml-2">l'action</span>
              </h1>
              <p className="text-white/80 mt-1 font-light">
                Partagez vos actions et engagements politiques
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Navigation par onglets - Style UDB */}
      <div className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('feed')}
              className={`py-6 px-2 border-b-2 font-medium text-base transition-colors flex items-center gap-2 ${
                activeTab === 'feed'
                  ? 'border-primary-blue text-primary-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Share size={18} />
              Fil d'actualité
            </button>
            <button
              onClick={() => setActiveTab('trending')}
              className={`py-6 px-2 border-b-2 font-medium text-base transition-colors flex items-center gap-2 ${
                activeTab === 'trending'
                  ? 'border-primary-yellow text-primary-yellow'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <TrendingUp size={18} />
              Tendances
            </button>
            <button
              onClick={() => setActiveTab('my_posts')}
              className={`py-6 px-2 border-b-2 font-medium text-base transition-colors flex items-center gap-2 ${
                activeTab === 'my_posts'
                  ? 'border-primary-green text-primary-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users size={18} />
              Mes publications
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
{/* Search Bar */}
        <div className="card-udb p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher dans les publications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-100 focus:border-primary-blue outline-none transition-all bg-white/70"
              />
            </div>
            <button className="flex items-center gap-2 px-6 py-3 border-2 border-gray-100 hover:border-primary-green hover:text-primary-green transition-all bg-white/70">
              <Filter size={20} />
              Filtres
            </button>
          </div>
        </div>

        {/* Contenu selon l'onglet actif */}
        {activeTab === 'feed' && (
          <div>
            {/* Créer une publication */}
            <CreatePostCard onCreatePost={handleCreatePost} />

            {/* Fil d'actualité */}
            <div>
              {mockPosts.map(post => (
                <PostCard
                  key={post.id}
                  post={post}
                  onLike={handleLike}
                  onComment={handleComment}
                />
              ))}
            </div>
          </div>
        )}

        {activeTab === 'trending' && (
          <div className="text-center py-12">
            <TrendingUp className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Tendances à venir</h3>
            <p className="text-gray-600">Les sujets les plus discutés apparaîtront ici.</p>
          </div>
        )}

        {activeTab === 'my_posts' && (
          <div className="text-center py-12">
            <Users className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Vos publications</h3>
            <p className="text-gray-600">Vos publications et activités apparaîtront ici.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialHub;